(() => {
  if (window._cxCardProductInit) return;
  window._cxCardProductInit = true;

  const ADDED_REVERT_MS = 1800;
  const openDropdowns = new Set();

  const closeAllDropdowns = (except = null) => {
    openDropdowns.forEach((dd) => {
      if (dd === except) return;
      const list = dd.querySelector('[data-cx-dropdown-list]');
      const toggle = dd.querySelector('[data-cx-dropdown-toggle]');
      dd.removeAttribute('data-cx-dropdown-open');
      if (list) list.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      openDropdowns.delete(dd);
    });
  };

  const setupDropdown = (card) => {
    const dropdown = card.querySelector('[data-cx-dropdown]');
    if (!dropdown) return null;

    const toggle = dropdown.querySelector('[data-cx-dropdown-toggle]');
    const labelEl = dropdown.querySelector('[data-cx-dropdown-label]');
    const list = dropdown.querySelector('[data-cx-dropdown-list]');
    const options = dropdown.querySelectorAll('[data-cx-dropdown-option]');
    if (!toggle || !labelEl || !list) return null;

    const defaultLabel = labelEl.textContent.trim();

    const open = () => {
      closeAllDropdowns(dropdown);
      dropdown.setAttribute('data-cx-dropdown-open', '');
      list.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      openDropdowns.add(dropdown);
    };

    const close = () => {
      dropdown.removeAttribute('data-cx-dropdown-open');
      list.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      openDropdowns.delete(dropdown);
    };

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (toggle.disabled) return;
      if (list.hidden) open();
      else close();
    });

    options.forEach((option) => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        if (option.classList.contains('cx-product-card__dropdown-option--disabled')) return;

        labelEl.textContent = option.dataset.label || defaultLabel;
        dropdown.dataset.selectedVariant = option.dataset.variantId || '';
        dropdown.dataset.selectedPrice = option.dataset.price || '';
        close();

        dropdown.dispatchEvent(new CustomEvent('cx:variant-change', {
          detail: {
            variantId: option.dataset.variantId,
            price: option.dataset.price,
            label: option.dataset.label,
          },
        }));
      });
    });

    return {
      dropdown,
      labelEl,
      defaultLabel,
      reset: () => {
        delete dropdown.dataset.selectedVariant;
        delete dropdown.dataset.selectedPrice;
        labelEl.textContent = defaultLabel;
      },
      shake: () => {
        toggle.classList.remove('cx-product-card__dropdown-toggle--shake');
        void toggle.offsetWidth;
        toggle.classList.add('cx-product-card__dropdown-toggle--shake');
      },
      clearShake: () => {
        toggle.classList.remove('cx-product-card__dropdown-toggle--shake');
      },
    };
  };

  const setupCard = (card) => {
    if (card.dataset.cxInit) return;
    card.dataset.cxInit = '1';

    const dd = setupDropdown(card);
    const button = card.querySelector('[data-cx-add-button]');
    const label = button?.querySelector('.cx-product-card__add-label');
    const popup = card.querySelector('[data-cx-popup]');

    if (!button || !label) return;

    const defaultButtonLabel = button.dataset.defaultLabel || 'ADD TO CART';
    let popupTimer = null;

    const showPopup = () => {
      if (!popup) return;
      popup.classList.add('is-visible');
      if (dd) dd.shake();

      clearTimeout(popupTimer);
      popupTimer = setTimeout(() => {
        popup.classList.remove('is-visible');
        if (dd) dd.clearShake();
      }, 2200);
    };

    const hidePopup = () => {
      if (!popup) return;
      popup.classList.remove('is-visible');
      if (dd) dd.clearShake();
      clearTimeout(popupTimer);
    };

    if (dd) {
      dd.dropdown.addEventListener('cx:variant-change', (e) => {
        const variantId = e.detail.variantId;
        if (!variantId) return;
        button.dataset.variantId = variantId;
        label.textContent = e.detail.price || defaultButtonLabel;
        hidePopup();
      });
    }

    button.addEventListener('click', async (e) => {
      e.preventDefault();
      if (button.disabled) return;

      const variantId = button.dataset.variantId;
      if (!variantId) {
        showPopup();
        return;
      }

      const priceLabel = label.textContent;
      button.disabled = true;
      button.classList.add('cx-product-card__add--loading');

      try {
        // Ask Shopify to bundle the drawer's re-rendered sections into the add
        // response, so the drawer can update and open without a page reload.
        const cartDrawer = document.querySelector('cart-drawer');
        const payload = { items: [{ id: variantId, quantity: 1 }] };
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Add to cart failed');
        }

        label.innerHTML = '<span class="cx-product-card__text-full">ADDED TO CART</span><span class="cx-product-card__text-short">ADDED</span>';
        button.classList.remove('cx-product-card__add--loading');
        button.classList.add('cx-product-card__add--added');

        const data = await res.json().catch(() => null);
        if (cartDrawer && data && data.sections) {
          try {
            // The outer <cart-drawer> element is not part of the re-rendered
            // section; when the cart was empty at page load its is-empty class
            // lingers and the CSS hides the header and line items.
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(data);
          } catch (renderErr) {
            refreshCartIndicators();
          }
        } else {
          refreshCartIndicators();
        }

        setTimeout(() => {
          label.textContent = priceLabel;
          button.classList.remove('cx-product-card__add--added');
          button.disabled = false;
        }, ADDED_REVERT_MS);
      } catch (err) {
        console.error('[cx-card-product] add to cart error:', err);
        label.textContent = 'TRY AGAIN';
        button.classList.remove('cx-product-card__add--loading');
        setTimeout(() => {
          label.textContent = priceLabel;
          button.disabled = false;
        }, ADDED_REVERT_MS);
      }
    });
  };

  const refreshCartIndicators = () => {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const count = cart.item_count;
        document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
          el.textContent = count;
        });
        document.querySelectorAll('.cart-count-bubble').forEach((el) => {
          if (count > 0) {
            el.classList.remove('hidden');
            el.style.display = '';
          }
        });
        const iconBubble = document.querySelector('#cart-icon-bubble');
        if (iconBubble && count > 0 && !iconBubble.querySelector('.cart-count-bubble')) {
          const bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = `<span aria-hidden="true">${count}</span>`;
          iconBubble.appendChild(bubble);
        }
      })
      .catch(() => {});
  };

  // Wire up cards added after initial load (e.g. "Load More" / infinite scroll,
  // or filtering) — these get appended into #product-grid without firing
  // DOMContentLoaded or shopify:section:load, so we catch them here.
  const observeGrid = () => {
    const grid = document.getElementById('product-grid');
    if (!grid || grid._cxObserved || !('MutationObserver' in window)) return;
    grid._cxObserved = true;
    new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches('[data-cx-product-card]')) setupCard(node);
          node.querySelectorAll('[data-cx-product-card]').forEach(setupCard);
        });
      });
    }).observe(grid, { childList: true });
  };

  const init = () => {
    document
      .querySelectorAll('[data-cx-product-card]')
      .forEach(setupCard);
    observeGrid();
  };

  // Global handlers — close dropdowns on outside click and Escape
  document.addEventListener('click', () => closeAllDropdowns());
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllDropdowns();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
